import type { Json } from '../../../services/supabase/database.types'
import type {
  RequisicaoDetail,
  RequisicaoDraft,
  RequisicaoDraftItem,
  RequisicaoItemRow,
} from '../types'

export function createEmptyItem(): RequisicaoDraftItem {
  return {
    id: crypto.randomUUID(),
    item_catalogo_id: null,
    persisted: false,
    versao: null,
    codigo: '',
    descricao: '',
    grade1: '',
    grade2: '',
    grade3: '',
    unidade: 'UN',
    quantidade_solicitada: '1',
    necessidade: '',
    prioridade: '',
    quantidade_comprada: null,
    fornecedor: null,
    prazo_entrega: null,
    status_item: 'Aguardando',
    observacao_comprador: null,
  }
}

export function createEmptyDraft(requisitanteNome: string, date = todayIso()): RequisicaoDraft {
  return {
    id: null,
    numero_requisicao: 'Gerado ao salvar',
    data_requisicao: date,
    obra: '',
    setor: '',
    requisitante_nome: requisitanteNome,
    status_geral: 'Nova Requisição',
    justificativa: '',
    solicitante_nome: requisitanteNome,
    responsavel_nome: '',
    bloqueada: false,
    versao: null,
    updated_at: null,
    itens: [createEmptyItem()],
    removedItens: [],
  }
}

export function itemRowToDraft(item: RequisicaoItemRow): RequisicaoDraftItem {
  return {
    id: item.id,
    item_catalogo_id: item.item_catalogo_id ?? null,
    persisted: true,
    versao: item.versao,
    codigo: item.codigo ?? '',
    descricao: item.descricao,
    grade1: item.grade1 ?? '',
    grade2: item.grade2 ?? '',
    grade3: item.grade3 ?? '',
    unidade: item.unidade ?? '',
    quantidade_solicitada: String(item.quantidade_solicitada),
    necessidade: item.necessidade ?? '',
    prioridade: normalizePriority(item.prioridade),
    quantidade_comprada: item.quantidade_comprada,
    fornecedor: item.fornecedor,
    prazo_entrega: item.prazo_entrega,
    status_item: item.status_item,
    observacao_comprador: item.observacao_comprador,
  }
}

export function detailToDraft(detail: RequisicaoDetail): RequisicaoDraft {
  const { requisicao } = detail
  return {
    id: requisicao.id,
    numero_requisicao: requisicao.numero_requisicao,
    data_requisicao: requisicao.data_requisicao,
    obra: requisicao.obra ?? '',
    setor: requisicao.setor ?? '',
    requisitante_nome: requisicao.requisitante_nome,
    status_geral: requisicao.status_geral,
    justificativa: requisicao.justificativa ?? '',
    solicitante_nome: requisicao.solicitante_nome ?? requisicao.requisitante_nome,
    responsavel_nome: requisicao.responsavel_nome ?? '',
    bloqueada: requisicao.bloqueada,
    versao: requisicao.versao,
    updated_at: requisicao.updated_at,
    itens: detail.itens.map(itemRowToDraft),
    removedItens: [],
  }
}

export function draftItemsToJson(items: RequisicaoDraftItem[]): Json {
  return items.map((item) => ({
    id: item.id,
    item_catalogo_id: item.item_catalogo_id,
    ...(item.versao === null ? {} : { versao: item.versao }),
    codigo: item.codigo.trim(),
    descricao: item.descricao.trim(),
    grade1: item.grade1.trim(),
    grade2: item.grade2.trim(),
    grade3: item.grade3.trim(),
    unidade: item.unidade.trim(),
    quantidade_solicitada: Number(item.quantidade_solicitada.replace(',', '.')),
    necessidade: item.necessidade.trim(),
    prioridade: item.prioridade,
  }))
}

export function todayIso(now = new Date()): string {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

function normalizePriority(value: string | null): RequisicaoDraftItem['prioridade'] {
  return value === 'BAIXA' || value === 'MÉDIA' || value === 'ALTA' || value === 'URGENTE' ? value : ''
}
